import { ContentFormatter } from "@ebscois/react-component-library";
import accordionProcessor from "./accordionProcessor";
import glossaryTooltipProcessor from "./glossaryTooltipProcessor";
import useInlineImageProcessor from "./useInlineImageProcessor";
import useInlineVideoProcessor from "./useInlineVideoProcessor";
import dhInternalLinkProcessor from "./dhInternalLinkProcessor";
import useLinkProcessor from "./useLinkProcessor";
import useDoiLinkProcessor from "./useDoiLinkProcessor";
import usePmidLinkProcessor from "./usePmidLinkProcessor";
import DialogProcessor from "./dialogProcessor";
import iconProcessor from "./iconProcessor";
import referenceTooltipProcessor from "./referenceTooltipProcessor";
import tableProcessor from "./tableProcessor";
import practicePointProcessor from "./practicePointProcessor";
import evidenceSynopsisProcessor from "./evidenceSynopsisProcessor";
import commentaryProcessor from "./commentaryProcessor";
import evidenceConclusionProcessor from "./evidenceConclusionProcessor";
import evidenceLevelBadgeProcessor from "./evidenceLevelBadgeProcessor";
import evidenceDetailsProcessor from "./evidenceDetailsProcessor";
import ufoProcessor from "./ufoProcessor";
import useDrugMappingProcessor from "./useDrugMappingProcessor";
import usePercLinkProcessor from "./usePercLinkProcessor";
import useHFCProcessor from "./useHFCProcessor";
import dateProcessor from "./dateProcessor";

export default function usePreprocessors({
  handleOpenPDF,
  handleOpenLightBoxImage,
  handleOpenLightBoxVideo,
  handleHFCDialogOpen,
  transformData,
  videos,
  images,
  algorithms,
  tablesContent,
  referenceData,
  openFtfDialog,
  setIsDialogOpen,
  setTableData,
  ancillaryData,
  referenceSectionId,
  setIsEvidenceBottomSheetOpen,
  updates,
  hfcData,
  isMobileDevice,
  isTabletDevice,
  loe,
  slugPubType,
  slugTitle,
  telemetryData,
  userExperience,
}) {
  const {
    glossary = {},
    supplemental = {},
    progressiveDisclosure = {},
    commentary = {},
    evidenceSynopsis = {},
    accordion,
  } = transformData || {};
  const { practicePoint } = progressiveDisclosure;
  const {
    processSecondaryContent,
    processAccordionContent,
  } = accordionProcessor({ supplemental });
  const { processGlossaryTooltip } = glossaryTooltipProcessor({ glossary });
  const { processInlineImage, imageAssets } = useInlineImageProcessor({
    handleOpenPDF,
    handleOpenLightBoxImage,
    images,
    pubType: slugPubType,
    algorithms,
  });
  const { processInlineVideo } = useInlineVideoProcessor({
    handleOpenLightBoxVideo,
    videos,
  });
  const { processDHInternalLink } = dhInternalLinkProcessor();
  const { processLinks } = useLinkProcessor({
    isMobileDevice,
    isTabletDevice,
    telemetryData,
  });
  const { processDoiLink } = useDoiLinkProcessor({
    openFtfDialog,
  });
  const { processPmidLink } = usePmidLinkProcessor({
    openFtfDialog,
  });
  const { processPercLink } = usePercLinkProcessor();
  const { processHfc } = useHFCProcessor({
    handleHFCDialogOpen,
    hfcData,
  });
  const { processModal, modalAssets } = DialogProcessor({ supplemental });
  const { processIcon } = iconProcessor();
  const { processDate } = dateProcessor();
  const { processReferenceTooltip } = referenceTooltipProcessor({
    referenceData,
  });
  const { processTable } = tableProcessor({ setIsDialogOpen, setTableData });
  const { processPracticePoint } = practicePointProcessor();
  const { processEvidenceSynopsis } = evidenceSynopsisProcessor();
  const { processCommentary } = commentaryProcessor();
  const { evidenceConclusion } = evidenceConclusionProcessor();
  const { evidenceLevelBadge } = evidenceLevelBadgeProcessor({
    setIsEvidenceBottomSheetOpen,
    loe,
  });

  const { evidenceDetails } = evidenceDetailsProcessor();
  const { ufo } = ufoProcessor({ updates });
  const wrapperNode = {
    type: "tag",
    name: "span",
    attribs: {},
  };

  const { processMappingLinks } = useDrugMappingProcessor({
    slugPubType,
    slugTitle,
    telemetryData,
  });

  const tooltipManager = (node, children) => {
    const dataFor = node.attribs["data-for"] || node.attribs["data-of-type"];
    if (dataFor === "glossary") {
      return processGlossaryTooltip(node, processContentPage);
    } else if (dataFor === "reference") {
      // the data-transform (UICM content) in DH needs to conform to the new standard used in DM.
      // Until that time, we have some work-around code to handle a difference in output
      const hasSuperscript =
        node.attribs["data-transform"] === "tooltip" ? false : true;
      return processReferenceTooltip(
        node,
        children,
        processContentPage,
        ContentFormatter.processDefaultNode,
        ancillaryData,
        referenceSectionId,
        hasSuperscript,
        isMobileDevice,
        isTabletDevice
      );
    } else {
      //There is nothing to do here so Sonar be happy please
    }
    return ContentFormatter.processDefaultNode(node, children);
  };

  const processNodes = (node, children) => {
    const preProcessorTrigger = node?.attribs?.["data-transform"];
    switch (preProcessorTrigger) {
      case "accordion":
        const accordionOfType = node.attribs["data-of-type"];
        if (accordionOfType === "secondary") {
          if (node.attribs["id"]) {
            wrapperNode.attribs["id"] = node.attribs["id"];
          }
          return processSecondaryContent(
            children,
            wrapperNode,
            ContentFormatter.processDefaultNode
          );
        }
        return processAccordionContent(
          node,
          processContentPage,
          accordion,
          children
        );
      case "inline-image":
      case "inline-algorithm":
        return processInlineImage(node);
      case "inline-video":
        return processInlineVideo(node);
      case "internal-link":
        return processDHInternalLink(node);
      case "link":
        return node?.attribs["data-type"] === "tooltip"
          ? tooltipManager(node, children)
          : processLinks({
              node,
              children,
              processDefault: ContentFormatter.processDefaultNode,
              wrapperNode,
              processNodes,
            });
      case "modal":
        return processModal(
          node,
          children,
          wrapperNode,
          processContentPage,
          processNodes
        );
      case "evidence-conclusion":
        return evidenceConclusion({
          node,
          children,
          wrapperNode,
          processNodes,
        });
      case "evidence-level-badge":
        return evidenceLevelBadge(node);
      case "evidence-details":
        return evidenceDetails(children);
      case "updateFunctionObject":
        return ufo(node, children);
      case "tooltip":
        return tooltipManager(node, children);
      case "icon":
        return processIcon(node);
      case "doi":
        return processDoiLink(node);
      case "full-text-container":
        return processPmidLink(node, children);
      case "table":
        // children prop should be removed after the new Tables approach is fully implemented on the middle side
        return processTable(
          node,
          {
            children,
            tablesContent,
          },
          processContentPage
        );
      case "practice-point":
        return processPracticePoint({
          node,
          children,
          wrapperNode,
          processNodes,
          practicePoint,
        });
      case "synopsis":
        return processEvidenceSynopsis({
          node,
          children,
          wrapperNode,
          processNodes,
          evidenceSynopsis,
        });
      case "commentary":
        return processCommentary({
          node,
          children,
          wrapperNode,
          processNodes,
          commentary,
        });
      case "mapping-link":
        return processMappingLinks({ node });
      case "perc-link":
        return processPercLink({ node, children });
      case "hfc":
        return processHfc();
      case "date":
        return processDate({ node });
      default:
        // If we didn't catch the specific `data-transform` value yet, just render it as any other element
        return ContentFormatter.processDefaultNode(node, children);
    }
  };

  const processContentPage = () => {
    return {
      replaceChildren: false,
      shouldProcessNode: (node) => {
        return !!node.attribs?.["data-transform"];
      },
      processNode: processNodes,
    };
  };

  return {
    modalAssets,
    imageAssets,
    processContentPage,
  };
}
